import { FilterX, Inbox, RotateCcw, SearchX, ServerCrash } from 'lucide-react';
import { createElement, isValidElement, type ComponentType, type ReactNode } from 'react';

import { cn } from '@/lib/cn';
import { Button } from './Button';

/**
 * EmptyState — the designed answer to "there is nothing to show".
 *
 * A restrained line-art mark in a tinted container, a real headline, one sentence of
 * explanation and the action that fills the void. Copy differs per variant because
 * "nothing yet" and "your filters are too tight" are different problems with different
 * next steps; pass `title`/`description` to override.
 */

export type EmptyStateVariant = 'default' | 'search' | 'filtered' | 'error';

/**
 * `dense` is the version that fits a 400px detail pane: no icon frame, a 28px
 * mark, body size for the headline, and the whole block flush left against the
 * same 16px gutter every other row in the pane uses. The `md` block is 200px+
 * tall and centred, which is right for a full page and wrong for a region that
 * has ~250px between a tab strip and a pinned composer — there it clipped its
 * own sentence mid-word.
 */
export type EmptyStateSize = 'sm' | 'md' | 'dense';

export interface EmptyStateProps {
  variant?: EmptyStateVariant;
  icon?: ComponentType<{ className?: string; strokeWidth?: number }> | ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  /** Primary action — pass a <Button>. */
  action?: ReactNode;
  secondaryAction?: ReactNode;
  /** Plural noun used by the default copy, e.g. "inquiries". */
  entity?: string;
  /** The search term, for the `search` variant's default headline. */
  query?: string;
  size?: EmptyStateSize;
  /**
   * `start` keeps the full-size block but ranges it left, for a region that is
   * itself left-aligned (a task queue, a log). `dense` is always `start`.
   */
  align?: 'center' | 'start';
  className?: string;
}

const VARIANT_ICON: Record<
  EmptyStateVariant,
  ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  default: Inbox,
  search: SearchX,
  filtered: FilterX,
  error: ServerCrash,
};

const VARIANT_TINT: Record<EmptyStateVariant, string> = {
  default: 'bg-[var(--surface-3)] text-muted',
  search: 'bg-[var(--info-soft)] text-[var(--info)]',
  filtered: 'bg-[var(--accent-soft)] text-accent-text',
  error: 'bg-[var(--danger-soft)] text-[var(--danger)]',
};

/** The same inks without the container, for the unframed `dense` mark. */
const VARIANT_INK: Record<EmptyStateVariant, string> = {
  default: 'text-muted',
  search: 'text-[var(--info)]',
  filtered: 'text-accent-text',
  error: 'text-[var(--danger)]',
};

function defaultCopy(
  variant: EmptyStateVariant,
  entity: string,
  query?: string,
): { title: string; description: string } {
  switch (variant) {
    case 'search':
      return {
        title: query ? `No ${entity} match “${query}”` : `No ${entity} match that search`,
        description:
          'Try a shorter term, or search by reference, company name or email address instead.',
      };
    case 'filtered':
      return {
        title: 'No results for these filters',
        description: `Every ${entity.replace(/s$/, '')} is excluded by at least one active filter. Loosen one, or clear them and start again.`,
      };
    case 'error':
      return {
        title: 'We could not load this',
        description:
          'The request failed before it finished. Retry now, or reload the page if it keeps happening.',
      };
    default:
      return {
        title: `No ${entity} yet`,
        description: `New ${entity} appear here the moment they arrive. You can also create one by hand.`,
      };
  }
}

export function EmptyState({
  variant = 'default',
  icon,
  title,
  description,
  action,
  secondaryAction,
  entity = 'records',
  query,
  size = 'md',
  align = 'center',
  className,
}: EmptyStateProps) {
  const copy = defaultCopy(variant, entity, query);
  const IconComponent = VARIANT_ICON[variant];
  const dense = size === 'dense';
  const start = dense || align === 'start';

  return (
    <div
      /* The handle `scripts/invariants-check.mjs` reads: it fails the build when
         an empty state's title leaves the documented grammar, or when the block
         grows a second action or a gold one. Not styling — a contract marker. */
      data-empty-state={variant}
      className={cn(
        'flex w-full flex-col',
        // Gaps rather than margins: the block keeps its rhythm whatever it is dropped into.
        dense
          ? 'items-start gap-2 px-4 py-4 text-left'
          : cn(
              'justify-center',
              start ? 'items-start text-left' : 'items-center px-6 text-center',
              size === 'sm' ? 'gap-4 py-8' : 'gap-5 py-12',
            ),
        className,
      )}
    >
      {dense ? (
        /* No frame: at 28px a lone mark reads as an illustration, and the 44px
           tinted container was pure height in a region that has none to spare. */
        <span aria-hidden className={cn('flex', VARIANT_INK[variant])}>
          <EmptyIcon icon={icon} fallback={IconComponent} size={size} />
        </span>
      ) : (
        <div className="relative">
          {/* Concentric hairline (4px inset against radius-lg → radius-xl outside). */}
          <span
            aria-hidden
            className="absolute -inset-1 rounded-[var(--radius-xl)] border border-line-subtle"
          />
          <span
            aria-hidden
            className={cn(
              'relative flex items-center justify-center rounded-[var(--radius-lg)]',
              size === 'sm' ? 'size-9' : 'size-11',
              VARIANT_TINT[variant],
            )}
          >
            <EmptyIcon icon={icon} fallback={IconComponent} size={size} />
          </span>
        </div>
      )}

      <div
        className={cn(
          'flex flex-col',
          dense ? 'items-start gap-1' : cn(start ? 'items-start' : 'items-center', 'gap-2'),
        )}
      >
        <h3
          className={cn(
            'text-balance font-semibold tracking-[var(--tracking-tight)] text-primary',
            dense ? 'text-base leading-[20px]' : size === 'sm' ? 'text-md' : 'text-lg',
          )}
        >
          {title ?? copy.title}
        </h3>
        {/*
          Left-aligned and measure-capped. Three ragged centred lines in a 368px
          column read as decoration, not as a sentence someone wrote.

          `dense` was capped at 36ch (252px) for the 400px detail pane, but the
          pane defaults to 0.38 of the viewport now — 547px at 1440, a 515px
          column — so the cap was breaking a one-line sentence over two ragged
          lines at 46% of the measure it had. 64ch (448px) still stops well short
          of an unreadable line and lets a sentence be a sentence; the pane's own
          16px gutters bound it below that.
        */}
        <p
          className={cn(
            'text-pretty text-base text-muted',
            dense
              ? 'max-w-[64ch] overflow-hidden leading-[20px] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3]'
              : 'max-w-[52ch] text-left leading-normal',
          )}
        >
          {description ?? copy.description}
        </p>
      </div>

      {action || secondaryAction ? (
        <div
          className={cn(
            'flex flex-wrap items-center gap-2',
            dense ? 'justify-start pt-1' : start ? 'justify-start' : 'justify-center',
          )}
        >
          {action}
          {secondaryAction}
        </div>
      ) : null}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
   The narrowed empty — ONE state, one wording, one affordance
   ──────────────────────────────────────────────────────────────────────────── */

/**
 * "The query and the filters on screen match nothing" is a single situation, and
 * before this existed nine surfaces described it five ways: two title grammars
 * (`Nothing matches “x”` on Inbox and Board against `No contacts match “x”` on
 * the rest), three button treatments for the identical Clear control
 * (`secondary` + icon, bare `secondary`, and a GOLD `primary` on the Board — which
 * made gold mean "recover" on one screen and "create" on the other eight), and one
 * screen offering a second action nobody else offered.
 *
 * Everything about that state now comes out of this component:
 *
 *   title        `No <plural> match “<query>”`  ·  `No <plural> match these filters`
 *   description  the remedy, and only the remedy — the title already said the verdict
 *   action       exactly one `secondary` Button with a `RotateCcw`, naming what it undoes
 *   second       there is none, on any surface
 *
 * The words are the same ones `CountLabel` prints in the toolbar two rows above
 * (`No contacts match`), so the page states the verdict once in two places rather
 * than twice in two dialects.
 */
export interface NarrowedEmptyStateProps {
  /** Plural noun of the collection: `contacts`, `inquiries`, `people`, `tags`. */
  noun: string;
  /** Singular, for the filtered description. Defaults to `noun` less a trailing `s`. */
  nounSingular?: string;
  /** The live search term. Empty/absent means only filters are narrowing. */
  query?: string;
  /** How many filter conditions are on. */
  filters?: number;
  /** Those conditions in the reader's words — `Europe, 5+ inquiries`. */
  filterSummary?: string;
  /**
   * One sentence on what the search actually covers, for the search branch. The
   * generic remedy is used when a surface has nothing more specific to say.
   */
  searchHint?: ReactNode;
  /** Clears the query AND the filters — one gesture, always both. */
  onClear: () => void;
  size?: EmptyStateSize;
  align?: 'center' | 'start';
  className?: string;
}

/**
 * What the one Clear button is called. It names what the reader actually did:
 * "Clear filters" under `No contacts match “zzzz”`, with no filter set, offers to
 * undo the one thing that never happened.
 */
export function clearActionLabel(query: string | undefined, filters = 0): string {
  const hasQuery = (query ?? '').trim().length > 0;
  if (hasQuery && filters > 0) return 'Clear search and filters';
  return hasQuery ? 'Clear search' : 'Clear filters';
}

export function NarrowedEmptyState({
  noun,
  nounSingular,
  query,
  filters = 0,
  filterSummary,
  searchHint,
  onClear,
  size = 'md',
  align = 'center',
  className,
}: NarrowedEmptyStateProps) {
  const term = (query ?? '').trim();
  const one = nounSingular ?? noun.replace(/s$/, '');
  const searching = term.length > 0;

  return (
    <EmptyState
      variant={searching ? 'search' : 'filtered'}
      entity={noun}
      query={searching ? term : undefined}
      size={size}
      align={align}
      className={className}
      title={searching ? `No ${noun} match “${term}”` : `No ${noun} match these filters`}
      description={
        searching
          ? (searchHint ?? 'Try fewer letters, or search by a reference, a company or an email domain.')
          : filterSummary
            ? `Your filters — ${filterSummary} — rule out every ${one}. Loosen one and the rest come back.`
            : `Your filters rule out every ${one}. Loosen one and the rest come back.`
      }
      action={
        <Button
          variant="secondary"
          size={size === 'md' ? 'md' : 'sm'}
          iconLeft={<RotateCcw aria-hidden />}
          onClick={onClear}
        >
          {clearActionLabel(term, filters)}
        </Button>
      }
    />
  );
}

function EmptyIcon({
  icon,
  fallback,
  size,
}: {
  icon?: ComponentType<{ className?: string; strokeWidth?: number }> | ReactNode;
  fallback: ComponentType<{ className?: string; strokeWidth?: number }>;
  size: EmptyStateSize;
}) {
  const className = size === 'dense' ? 'size-7' : size === 'sm' ? 'size-4' : 'size-5';
  if (!icon) return createElement(fallback, { className, strokeWidth: 1.5 });
  /*
   * Elements first, *then* component types. `typeof icon === 'function'` was the
   * only component branch, and every lucide icon is a `forwardRef` object rather
   * than a function — so every caller that passed `icon={Paperclip}` silently
   * fell through to the variant fallback and three unrelated tabs all drew the
   * same generic tray.
   */
  if (isValidElement(icon)) return <>{icon}</>;
  return createElement(icon as ComponentType<{ className?: string; strokeWidth?: number }>, {
    className,
    strokeWidth: 1.5,
  });
}
